/**
 * Utility functions for smart comparison and synchronization of Advertisements & Shimmer Sizing.
 * 
 * Ensures:
 * 1. Zero unnecessary re-renders: If backend ads haven't changed, React state is not mutated.
 * 2. Zero unnecessary localStorage writes: localStorage is only updated when ad shimmer sizing actually changes.
 * 3. Dedicated lightweight key (`mr_ad_ratios`): SplashData is NEVER overwritten or re-serialized.
 * 4. Shimmer stability: Preserves initial aspect ratio map so shimmers don't resize or glitch on render.
 */

const AD_RATIOS_KEY = "mr_ad_ratios";

/**
 * Compares two advertisement objects to determine if they are identical.
 * Handles null/undefined and performs deep JSON equality check.
 * 
 * @param {Object|null} prevAds 
 * @param {Object|null} nextAds 
 * @returns {boolean} True if ads are identical, false if changed
 */
export function areAdsEqual(prevAds, nextAds) {
  if (prevAds === nextAds) return true;
  if (!prevAds && !nextAds) return true;
  if (!prevAds || !nextAds) return false;

  try {
    return JSON.stringify(prevAds) === JSON.stringify(nextAds);
  } catch (e) {
    return false;
  }
}

/**
 * Retrieves the cached media_size aspect ratio for a specific ad placement.
 * Reads from the dedicated lightweight `mr_ad_ratios` key with fallback to initial SplashData.
 * 
 * @param {string} placementKey - Placement key (e.g., 'home_center', 'agenda_center', 'home_right', 'home_left')
 * @param {number} defaultRatio - Fallback ratio if no stored size is found
 * @returns {number} Aspect ratio
 */
export function getStoredAdRatio(placementKey, defaultRatio = 1.5) {
  try {
    // 1. Check dedicated lightweight ratio map
    const ratios = JSON.parse(localStorage.getItem(AD_RATIOS_KEY) || "{}");
    const storedRatio = parseFloat(ratios?.[placementKey]);
    if (Number.isFinite(storedRatio) && storedRatio > 0) {
      return storedRatio;
    }

    // 2. Fallback to initial SplashData if available
    const splash = JSON.parse(localStorage.getItem("SplashData") || "{}");
    const ads = splash?.ads || {};
    const placementAds = ads[placementKey];
    const firstItem = Array.isArray(placementAds) ? placementAds[0] : placementAds;
    const actualAd = firstItem?.general_ad || firstItem;
    const parsedSize = parseFloat(actualAd?.media_size);
    if (Number.isFinite(parsedSize) && parsedSize > 0) {
      return parsedSize;
    }
  } catch (e) {
    // ignore
  }
  return defaultRatio;
}

/**
 * Synchronizes ad media_size (aspect ratio) in a dedicated lightweight key (`mr_ad_ratios`).
 * NEVER modifies or rewrites the heavy SplashData object.
 *
 * @param {string} placementKey - e.g. "home_center", "agenda_center", "home_right", "home_left"
 * @param {number|string} newMediaSize - The incoming aspect ratio from page/splash API
 * @returns {boolean} True if ratios were updated, false if unchanged
 */
export function syncAdMediaSize(placementKey, newMediaSize) {
  const parsedNewSize = parseFloat(newMediaSize);
  if (!Number.isFinite(parsedNewSize) || parsedNewSize <= 0) return false;

  try {
    const ratios = JSON.parse(localStorage.getItem(AD_RATIOS_KEY) || "{}");
    const currentStoredSize = parseFloat(ratios?.[placementKey]);

    // If size is unchanged, do not touch localStorage!
    if (Number.isFinite(currentStoredSize) && Math.abs(currentStoredSize - parsedNewSize) < 0.001) {
      return false;
    }

    // Update ONLY the dedicated tiny ratio map (~50 bytes)
    ratios[placementKey] = parsedNewSize;
    localStorage.setItem(AD_RATIOS_KEY, JSON.stringify(ratios));
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Conditionally syncs new splash API payload.
 * - Updates in-memory React State (SplashContext) for live UI rendering.
 * - Updates ONLY the tiny `mr_ad_ratios` map if aspect ratios changed.
 * - NEVER rewrites or mutates the main SplashData object.
 *
 * @param {Object} newJson - Fresh response from SplashApi
 * @param {Object|null} currentSplashResult - Current in-memory splashResult
 * @param {Function} setSplashResult - React state setter for splashResult
 * @param {Function} [onAdsChanged] - Optional callback when ads change
 * @returns {boolean} True if state was updated, false if skipped (identical)
 */
export function syncSplashData(newJson, currentSplashResult, setSplashResult, onAdsChanged) {
  if (!newJson || !newJson.status) return false;

  let currentAds = currentSplashResult?.ads;
  if (!currentAds) {
    try {
      const stored = JSON.parse(localStorage.getItem("SplashData") || "{}");
      currentAds = stored?.ads || null;
    } catch {
      currentAds = null;
    }
  }

  const newAds = newJson?.ads || null;
  const adsChanged = !areAdsEqual(currentAds, newAds);

  if (adsChanged) {
    // 1. Update React memory state (instantly updates live UI with zero disk IO)
    if (typeof setSplashResult === "function") {
      setSplashResult(newJson);
    }

    // 2. Sync only the lightweight shimmer aspect ratios in mr_ad_ratios
    if (newAds && typeof newAds === "object") {
      Object.entries(newAds).forEach(([placementKey, placementAds]) => {
        const firstItem = Array.isArray(placementAds) ? placementAds[0] : placementAds;
        const actualAd = firstItem?.general_ad || firstItem;
        const parsedSize = parseFloat(actualAd?.media_size);
        if (Number.isFinite(parsedSize) && parsedSize > 0) {
          syncAdMediaSize(placementKey, parsedSize);
        }
      });
    }

    if (typeof onAdsChanged === "function") {
      onAdsChanged(newJson);
    }
    return true;
  }

  return false;
}
